import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';

import { i18n } from '@/utils/i18n';

// Schemas
// NineNintyForm
const nineNintySchema = Yup.object().shape({
  nineNintyForm: Yup.mixed<File | string>().required(i18n.t('sign_up.nonProfit.wizard.errors.nineNintyRequested')),
  newOrgCheck: Yup.boolean().default(false)
});

// GeneralData
const generalSchema = Yup.object().shape({
  name: Yup.string().required(i18n.t('sign_up.nonProfit.wizard.errors.general.nameRequested')),
  description: Yup.string(),
  email: Yup.string().email(i18n.t('mix.valid_email')).required(i18n.t('mix.required_email')),
  location: Yup.string().required(),
  address: Yup.string().required(i18n.t('sign_up.nonProfit.wizard.errors.general.address')),
  showMap: Yup.boolean().nullable(),
  totalEmployees: Yup.number()
    .typeError(i18n.t('sign_up.nonProfit.wizard.errors.general.numberEmployeesRequested'))
    .moreThan(-1, i18n.t('mix.positive_number'))
    .required(i18n.t('sign_up.nonProfit.wizard.errors.general.numberEmployeesNumber')),
  totalVolunteers: Yup.number()
    .typeError(i18n.t('sign_up.nonProfit.wizard.errors.general.numberVolunteersNumber'))
    .moreThan(-1, i18n.t('mix.positive_number'))
    .required(i18n.t('sign_up.nonProfit.wizard.errors.general.numberVolunteersRequested')),
  thumbnail: Yup.array()
    .of(Yup.string().nullable())
    .min(1, i18n.t('sign_up.nonProfit.wizard.errors.general.minThumbsRequested'))
});

// Revenue
const revenueSchema = Yup.object().shape({
  revenue: Yup.number()
    .typeError(i18n.t('sign_up.nonProfit.wizard.errors.revenueNumber'))
    .moreThan(-1, i18n.t('mix.positive_number'))
    .required(i18n.t('sign_up.nonProfit.wizard.errors.revenueRequested')),
  totalExpenses: Yup.number()
    .typeError(i18n.t('sign_up.nonProfit.wizard.errors.expensesNumber'))
    .moreThan(-1, i18n.t('mix.positive_number'))
    .required(i18n.t('sign_up.nonProfit.wizard.errors.expensesRequested'))
});

// BackgroundChecks
const backgroundChecksSchema = Yup.object().shape({
  employees: Yup.boolean().required(i18n.t('sign_up.nonProfit.wizard.errors.background.employeesRequested')),
  volunteers: Yup.boolean().required(i18n.t('sign_up.nonProfit.wizard.errors.background.volunteersRequested'))
});

// BoardOfDirectors
// Director
const directorSchema = Yup.object().shape({
  position: Yup.string().required(i18n.t('sign_up.nonProfit.wizard.errors.boardDirector.jobRequested')),
  fullName: Yup.string().required(i18n.t('sign_up.nonProfit.wizard.errors.boardDirector.titleRequested')),
  annualSalary: Yup.number()
    .transform((value, originalValue) => (String(originalValue).trim() === '' ? undefined : value))
    .typeError(i18n.t('sign_up.nonProfit.wizard.errors.boardDirector.jobNumber'))
    .moreThan(-1, i18n.t('mix.positive_number'))
    .optional()
    .nullable(),
  allowance: Yup.number()
    .transform((value, originalValue) => (String(originalValue).trim() === '' ? undefined : value))
    .typeError(i18n.t('sign_up.nonProfit.wizard.errors.boardDirector.stipendNumber'))
    .moreThan(-1, i18n.t('mix.positive_number'))
    .optional()
    .nullable()
});

// MainForm
const boardOfDirectorsSchema = Yup.object().shape({
  boardDirectories: Yup.array()
    .of(directorSchema)
    .when('$isChurch', {
      is: (isChurch: boolean | undefined) => isChurch !== true,
      then: (schema) => schema.min(1, i18n.t('sign_up.nonProfit.wizard.errors.boardDirector.boardRequested')),
      otherwise: (schema) => schema
    })
    .required()
});

// Pastors
// Pastor
const pastorSchema = Yup.object().shape({
  position: Yup.string().required(i18n.t('sign_up.nonProfit.wizard.errors.pastor.jobRequested')),
  fullName: Yup.string().required(i18n.t('sign_up.nonProfit.wizard.errors.pastor.titleRequested')),
  annualSalary: Yup.number()
    .transform((value, originalValue) => (String(originalValue).trim() === '' ? undefined : value))
    .typeError(i18n.t('sign_up.nonProfit.wizard.errors.pastor.jobNumber'))
    .moreThan(-1, i18n.t('mix.positive_number'))
    .optional()
    .nullable(),
  allowance: Yup.number()
    .transform((value, originalValue) => (String(originalValue).trim() === '' ? undefined : value))
    .typeError(i18n.t('sign_up.nonProfit.wizard.errors.pastor.stipendNumber'))
    .moreThan(-1, i18n.t('mix.positive_number'))
    .optional()
    .nullable()
});

// MainForm — CHURCH-only screen
const pastorsSchema = Yup.object().shape({
  pastors: Yup.array()
    .of(pastorSchema)
    .min(1, i18n.t('sign_up.nonProfit.wizard.errors.pastor.pastorRequested'))
    .required()
});

// TotalSalaries
const totalSalariesSchema = Yup.object().shape({
  totalSalaries: Yup.number()
    .typeError(i18n.t('sign_up.nonProfit.wizard.errors.totalSalaries.mustBeNumber'))
    .moreThan(-1, i18n.t('mix.positive_number'))
    .required(i18n.t('sign_up.nonProfit.wizard.errors.totalSalaries.requested')),
  otherEmployeesBenefits: Yup.number()
    .typeError(i18n.t('sign_up.nonProfit.wizard.errors.benefits.mustBeNumber'))
    .moreThan(-1, i18n.t('mix.positive_number'))
    .required(i18n.t('sign_up.nonProfit.wizard.errors.benefits.requested'))
});

// PL
const pLSchema = Yup.object().shape({
  pL: Yup.string().nullable(),
  balanceSheet: Yup.string().nullable()
});

// ThirdPartyAudit
const thirdPartyAuditSchema = Yup.object().shape({
  thirdPartyAuditDeny: Yup.boolean().required(i18n.t('sign_up.nonProfit.wizard.errors.thirdPartyAudit')),
  thirdPartyAudit: Yup.string()
    .nullable()
    .when('thirdPartyAuditDeny', {
      is: true,
      then: (schema) => schema.required(i18n.t('sign_up.nonProfit.wizard.errors.thirdPartyAuditRequired'))
    })
});

// ImpactStudy
const impactStudySchema = Yup.object().shape({
  impactStudyDeny: Yup.boolean().required(i18n.t('sign_up.nonProfit.wizard.errors.impactStudy')),
  impactStudy: Yup.string()
    .nullable()
    .when('impactStudyDeny', {
      is: true,
      then: (schema) => schema.required(i18n.t('sign_up.nonProfit.wizard.errors.impactStudyRequired'))
    })
});

// Salaries
// Salary
const salarySchema = Yup.object().shape({
  fullName: Yup.string()
    .test('empty-check', (value) => !value || value !== undefined)
    .nonNullable()
    .required(i18n.t('sign_up.nonProfit.wizard.errors.salaries.nameRequested')),
  position: Yup.string()
    .test('empty-check', (value) => !value || value !== undefined)
    .nonNullable()
    .required(i18n.t('sign_up.nonProfit.wizard.errors.salaries.jobTitle')),
  annualSalary: Yup.number()
    .transform((value, originalValue) => (String(originalValue).trim() === '' ? undefined : value))
    .typeError(i18n.t('sign_up.nonProfit.wizard.errors.salaries.annualNumber'))
    .moreThan(-1, i18n.t('mix.positive_number'))
    .optional()
    .nullable(),
  allowance: Yup.number()
    .transform((value, originalValue) => (String(originalValue).trim() === '' ? undefined : value))
    .typeError(i18n.t('sign_up.nonProfit.wizard.errors.salaries.housingNumber'))
    .moreThan(-1, i18n.t('mix.positive_number'))
    .optional()
    .nullable()
});

// MainForm
const salariesSchema = Yup.object().shape({
  salaries: Yup.array().of(salarySchema).max(10, i18n.t('sign_up.nonProfit.wizard.errors.salaries.max')).required()
});

// LandEquipments
const landEquipmentsSchema = Yup.object().shape({
  totalLandBuildingEquiment: Yup.number()
    .typeError(i18n.t('sign_up.nonProfit.wizard.errors.totalLand.mustBeNumber'))
    .moreThan(-1, i18n.t('mix.positive_number'))
    .required(i18n.t('sign_up.nonProfit.wizard.errors.totalLand.requested'))
});

// FundRaising
const fundRaisingSchema = Yup.object().shape({
  totalFundraisingMarketing: Yup.number()
    .typeError(i18n.t('sign_up.nonProfit.wizard.errors.totalFundraising.mustBeNumber'))
    .moreThan(-1, i18n.t('mix.positive_number'))
    .required(i18n.t('sign_up.nonProfit.wizard.errors.totalFundraising.requested'))
});

// Programs
// Program
const programSchema = Yup.object().shape({
  title: Yup.string().nullable(),
  name: Yup.string().required(i18n.t('sign_up.nonProfit.wizard.errors.programs.name')),
  description: Yup.string().nullable(),
  programDate: Yup.string().nullable(),
  budget: Yup.number()
    .typeError(i18n.t('sign_up.nonProfit.wizard.errors.programs.mustBeNumber'))
    .moreThan(-1, i18n.t('mix.positive_number'))
    .optional()
    .nullable(),
  address: Yup.string().nullable(),
  location: Yup.string().nullable()
});

// MainForm — CHURCH-only screen, array is always optional
const programsSchema = Yup.object().shape({
  programs: Yup.array().of(programSchema).required(),
  byLaws: Yup.string().optional().nullable()
});

// TotalGrantsPrograms
// TotalGrantProgram
const totalGrantProgramSchema = Yup.object().shape({
  name: Yup.string().required(i18n.t('sign_up.nonProfit.wizard.errors.totalGrantsPrograms.name')),
  description: Yup.string().nullable(),
  budget: Yup.number()
    .typeError(i18n.t('sign_up.nonProfit.wizard.errors.totalGrantsPrograms.mustBeNumber'))
    .moreThan(-1, i18n.t('mix.positive_number'))
    .required(i18n.t('sign_up.nonProfit.wizard.errors.totalGrantsPrograms.amountSpent'))
});

// MainForm
const totalGrantsProgramsSchema = Yup.object().shape({
  totalGrantsPrograms: Yup.number()
    .typeError(i18n.t('sign_up.nonProfit.wizard.errors.totalGrantsPrograms.mustBeNumber'))
    .moreThan(-1, i18n.t('mix.positive_number'))
    .required(i18n.t('sign_up.nonProfit.wizard.errors.totalGrantsPrograms.totalGrants')),
  grantsPrograms: Yup.array()
    .of(totalGrantProgramSchema)
    .min(1, i18n.t('sign_up.nonProfit.wizard.errors.totalGrantsPrograms.minimal'))
    .required()
});

// LightRatingQuestions
// LightRatingQuestion
const lightRatingQuestionSchema = Yup.object().shape({
  id: Yup.string().required(),
  answer: Yup.string().nullable().default(null)
});

// MainForm
const lightRatingQuestionsSchema = Yup.object().shape({
  questions: Yup.array().of(lightRatingQuestionSchema).required()
});

// Types
// NineNintyForm
export type NineNintyData = Yup.InferType<typeof nineNintySchema>;
// GeneralData
export type GeneralData = Yup.InferType<typeof generalSchema>;
// Revenue
export type RevenueData = Yup.InferType<typeof revenueSchema>;
// BackgroundChecks
export type BackgroundChecksData = Yup.InferType<typeof backgroundChecksSchema>;
// BoardOfDirectors
export type BoardOfDirectorsData = Yup.InferType<typeof boardOfDirectorsSchema>;
export type DirectorData = Yup.InferType<typeof directorSchema>;
// Pastors
export type PastorsData = Yup.InferType<typeof pastorsSchema>;
export type PastorData = Yup.InferType<typeof pastorSchema>;
// TotalSalaries
export type TotalSalariesData = Yup.InferType<typeof totalSalariesSchema>;
// PL
export type PLData = Yup.InferType<typeof pLSchema>;
// ThirdPartyAudit
export type ThirdPartyAuditData = Yup.InferType<typeof thirdPartyAuditSchema>;
// ImpactStudy
export type ImpactStudyData = Yup.InferType<typeof impactStudySchema>;
// Salaries
export type SalariesData = Yup.InferType<typeof salariesSchema>;
export type SalaryData = Yup.InferType<typeof salarySchema>;
// LandEquipments
export type LandEquipmentsData = Yup.InferType<typeof landEquipmentsSchema>;
// FundRaising
export type FundRaisingData = Yup.InferType<typeof fundRaisingSchema>;
// Programs
export type ProgramsData = Yup.InferType<typeof programsSchema>;
export type ProgramData = Yup.InferType<typeof programSchema>;
// TotalGrantsProgram
export type TotalGrantsProgramsData = Yup.InferType<typeof totalGrantsProgramsSchema>;
export type TotalGrantProgramData = Yup.InferType<typeof totalGrantProgramSchema>;
//LightRatingQuestions
export type LightRatingQuestionsData = Yup.InferType<typeof lightRatingQuestionsSchema>;
export type LightRatingQuestionData = Yup.InferType<typeof lightRatingQuestionSchema>;

// Resolvers
// NineNintyForm
export const nineNintyResolver = yupResolver(nineNintySchema);
// GeneralData
export const generalResolver = yupResolver(generalSchema);
// Revenue
export const revenueResolver = yupResolver(revenueSchema);
// BackgroundChecks
export const backgroundChecksResolver = yupResolver(backgroundChecksSchema);
// BoardOfDirectors
export const boardOfDirectorsResolver = yupResolver(boardOfDirectorsSchema);
export const directorResolver = yupResolver(directorSchema);
// Pastors
export const pastorsResolver = yupResolver(pastorsSchema);
export const pastorResolver = yupResolver(pastorSchema);
// TotalSalaries
export const totalSalariesResolver = yupResolver(totalSalariesSchema);
// PL
export const pLResolver = yupResolver(pLSchema);
// ThirdPartyAudit
export const thirdPartyAuditResolver = yupResolver(thirdPartyAuditSchema);
// ImpactStudy
export const impactStudyResolver = yupResolver(impactStudySchema);
// Salaries
export const salariesResolver = yupResolver(salariesSchema);
export const salaryResolver = yupResolver(salarySchema);
// LandEquipments
export const landEquipmentsResolver = yupResolver(landEquipmentsSchema);
// FundRaising
export const fundRaisingResolver = yupResolver(fundRaisingSchema);
// Programs
export const programsResolver = yupResolver(programsSchema);
export const programResolver = yupResolver(programSchema);
// TotalGrantsPrograms
export const totalGrantsProgramsResolver = yupResolver(totalGrantsProgramsSchema);
export const totalGrantProgramResolver = yupResolver(totalGrantProgramSchema);
// LightRatingQuestions
export const lightRatingQuestionsResolver = yupResolver(lightRatingQuestionsSchema);
export const lightRatingQuestionResolver = yupResolver(lightRatingQuestionSchema);
